import { useCallback, useMemo, useState } from "react";
import { Form } from "antd";
import type { FormInstance } from "antd";
import { toast } from "react-toastify";
import { customLogger } from "../../../../../utils/Logger";
import { getApiErrorMessage } from "../../../../../utils/apiError";
import MY_DEVICES from "../constants";
import { ActivationState, DeviceFilter, DeviceOrder } from "../types";
import { getOrderStage } from "../utils/orderStage";

interface UseMyDevicesOptions {
  orders: DeviceOrder[];
  onActivateDevice: (
    orderId: string,
    values: { macAddress: string },
  ) => Promise<void>;
}

interface UseMyDevicesResult {
  activeFilter: DeviceFilter;
  activationState: ActivationState | null;
  submitting: boolean;
  activationForm: FormInstance<{ macAddress: string }>;
  counts: Record<DeviceFilter, number>;
  filteredOrders: DeviceOrder[];
  setActiveFilter: (filter: DeviceFilter) => void;
  openActivation: (order: DeviceOrder) => void;
  handleCloseModal: () => void;
  handleActivate: (values: { macAddress: string }) => Promise<void>;
}

const useMyDevices = ({
  orders,
  onActivateDevice,
}: UseMyDevicesOptions): UseMyDevicesResult => {
  const [activeFilter, setActiveFilter] = useState<DeviceFilter>(
    MY_DEVICES.DEFAULT_FILTER,
  );
  const [activationState, setActivationState] =
    useState<ActivationState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activationForm] = Form.useForm<{ macAddress: string }>();

  const counts = useMemo<Record<DeviceFilter, number>>(
    () => ({
      all: orders.length,
      "in-process": orders.filter((o) => getOrderStage(o) === "in-process")
        .length,
      shipped: orders.filter((o) => getOrderStage(o) === "shipped").length,
      delivered: orders.filter((o) => getOrderStage(o) === "delivered").length,
      active: orders.filter((o) => getOrderStage(o) === "active").length,
    }),
    [orders],
  );

  const filteredOrders = useMemo(
    () =>
      activeFilter === "all"
        ? orders
        : orders.filter((o) => getOrderStage(o) === activeFilter),
    [activeFilter, orders],
  );

  const openActivation = useCallback((order: DeviceOrder) => {
    setActivationState({ order, open: true });
  }, []);

  const handleCloseModal = useCallback(() => {
    setActivationState(null);
    activationForm.resetFields();
  }, [activationForm]);

  const handleActivate = useCallback(
    async (values: { macAddress: string }) => {
      if (!activationState) return;

      setSubmitting(true);
      try {
        await onActivateDevice(activationState.order.id, values);
        handleCloseModal();
      } catch (error: unknown) {
        customLogger(error);
        toast.error(getApiErrorMessage(error, MY_DEVICES.LABELS.ERROR_ACTIVATE));
      } finally {
        setSubmitting(false);
      }
    },
    [activationState, onActivateDevice, handleCloseModal],
  );

  return {
    activeFilter,
    activationState,
    submitting,
    activationForm,
    counts,
    filteredOrders,
    setActiveFilter,
    openActivation,
    handleCloseModal,
    handleActivate,
  };
};

export default useMyDevices;
